<template>
    <div class="record-cost-units loading-content-container">
        <div v-if="costUnitListLoading > 0" class="loading-overlay d-flex justify-content-center pt-5">
            <csp-loading-indicator/>
        </div>
        <b-row v-if="costUnitListLoading === 0" class="mb-4">
            <csp-desktop-info-card
                :cols="12"
                icon="inticon-users"
                title="Schadenpositionen">
                <div class="d-flex justify-content-end">
                    <csp-payment-request-modal
                        v-if="permissions.canShowPaymentRequests"
                        :record-id="recordId"
                        @payment-request-sent="loadCostUnits"/>
                    <csp-record-detail-cost-units-create
                        v-if="permissions.canCreateCostUnit && createModalAvailable"
                        :form-context="creationFormContext"
                        :hide-for-group-options="hideForGroupOptions"
                        :record-id="recordId"
                        @created="loadCostUnits();resetCreateModal()"/>
                </div>
                <csp-record-detail-cost-unit-add-payment-modal
                    v-if="addPaymentModalCostUnitId"
                    :cost-unit-id="addPaymentModalCostUnitId"
                    :form-context="costUnitPaymentFormContext"
                    :is-modal-open="shouldShowAddPaymentModal"
                    :record-id="recordId"
                    @close="resetAddCarDealerPaymentModal"
                    @updated="loadCostUnits()"/>
                <csp-record-detail-cost-unit-edit-modal
                    v-if="editModalCostUnitId"
                    :cost-unit-id="editModalCostUnitId"
                    :form-context="costUnitFormContext"
                    :hide-for-group-options="hideForGroupOptions"
                    :is-modal-open="shouldShowCostUnitEditModal"
                    :record-id="recordId"
                    @close="resetEditModal"
                    @updated="loadCostUnits()"/>
                <csp-record-detail-cost-unit-draft-edit-modal
                    v-if="editModalCostUnitDraftId"
                    :cost-unit-draft-id="editModalCostUnitDraftId"
                    :form-context="costUnitDraftFormContext"
                    :hide-for-group-options="hideForGroupOptions"
                    :is-modal-open="shouldShowCostUnitDraftEditModal"
                    :record-id="recordId"
                    @close="resetEditModal"
                    @updated="loadCostUnits"/>
                <csp-record-detail-cost-unit-draft-detail-modal
                    v-if="detailModalCostUnitDraftId"
                    :cost-unit-draft-id="detailModalCostUnitDraftId"
                    :hide-for-group-options="hideForGroupOptions"
                    :is-modal-open="shouldShowCostUnitDraftDetailModal"
                    :record-id="recordId"
                    @close="resetDetailModal"/>
                <csp-record-detail-cost-unit-detail-modal
                    :cost-unit-id="detailModalCostUnitId"
                    :hide-for-group-options="hideForGroupOptions"
                    :is-modal-open="shouldShowCostUnitDetailModal"
                    :record-id="recordId"
                    @close="resetDetailModal"
                    @updated="loadCostUnits()"/>
                <div class="cost-unit-list-table">
                    <csp-record-detail-cost-units-list
                        :cost-unit-drafts="costUnitDrafts"
                        :cost-units="costUnits"
                        :hide-for-group-options="hideForGroupOptions"
                        @cost-unit-add-car-dealer-payment="addCarDealerPayment"
                        @cost-unit-delete="deleteCostUnit"
                        @cost-unit-details="showCostUnitDetails"
                        @cost-unit-edit="showCostUnitEdit"
                        @cost-unit-verify="verifyCostUnit"/>
                </div>

                <div v-if="costUnits.length" class="cost-unit-legends collapse-body mt-5">
                    <div class="row">
                        <div class="legends col-md-4">
                            <div class="legend">
                                <div class="row">
                                    <div class="col-2 text-right">
                                        <span class="legend-symbol legend-adjustment">*</span>
                                    </div>
                                    <div class="col-10">
                                        <p>Betrag beinhaltet akzeptierte Minderung</p>
                                    </div>
                                </div>
                            </div>
                            <div class="legend">
                                <div class="row">
                                    <div class="col-2">
                                        <span class="legend-symbol legend-insurance">&nbsp;</span>
                                    </div>
                                    <div class="col-10">
                                        <p>von der Versicherung zu begleichende Forderungen</p>
                                    </div>
                                </div>
                            </div>
                            <div v-if="contextData" class="mt-2">
                                <div>
                                    Kunde ist vorsteuerabzugsberechtigt:
                                    {{ contextData.customerIsVatDeductionEntitled ? 'Ja' : 'Nein' }}
                                </div>
                                <div>
                                    Unfalldatum:
                                    {{ formatDate(contextData.accidentDate) }}
                                </div>
                            </div>
                        </div>
                        <div class="col-md-8">
                            <csp-record-detail-cost-units-summary :cost-unit-summary="costUnitSums"/>
                        </div>
                    </div>
                </div>
            </csp-desktop-info-card>
        </b-row>
        <b-row v-if="costUnitListLoading === 0 && permissions.canShowSapPayments" class="mt-4">
            <b-col cols="8">
                <csp-table
                    ref="sapPaymentsTable"
                    icon="inticon-autoclaim_money"
                    :items-ajax-url="sapPaymentsAjaxUrl"
                    :row-class="sapPaymentsRowClass"
                    table-headline="Nicht zugeordnete SAP Zahlungen">
                    <template v-if="permissions.canCreateSapPayments" v-slot:above-body>
                        <b-button class="mb-3 right" variant="primary" @click="createCostUnitPaymentsFromSap">
                            Neu zuordnen
                        </b-button>
                    </template>
                </csp-table>
            </b-col>
        </b-row>
        <b-row
            v-if="costUnitListLoading === 0 && recordLawsuit"
            class="mt-4">
            <csp-record-lawsuit
                v-if="recordLawsuit && initialFormContexts.recordLawsuit.isLoaded"
                :cols="8"
                :form-context="initialFormContexts.recordLawsuit"/>
        </b-row>
        <b-row
            v-if="costUnitListLoading === 0 && appraisals && appraisals.length > 0"
            class="mt-4">
            <csp-desktop-info-card
                :cols="8"
                icon="inticon-autoclaim_billing"
                title="Forderungen TÜV SÜD">
                <csp-record-appraisal-list
                    :appraisals="appraisals"
                    :record-id="recordId"/>
            </csp-desktop-info-card>
        </b-row>
        <b-row
            v-if="costUnitListLoading === 0 && (hasBankAccountToShow || showRegressData)"
            class="mt-4">
            <b-col v-if="appraiserBankAccount" cols="4">
                <csp-record-detail-cost-unit-bank-account
                    :bank-account="appraiserBankAccount"
                    headline="Bankverbindung Gutachter"/>
            </b-col>
            <b-col v-if="carDealerBankAccount" cols="4">
                <csp-record-detail-cost-unit-bank-account
                    :bank-account="carDealerBankAccount"
                    headline="Bankverbindung Autohaus"/>
            </b-col>
            <b-col v-if="customerBankAccount" cols="4">
                <csp-record-detail-cost-unit-bank-account
                    :bank-account="customerBankAccount"
                    headline="Bankverbindung Kunde"/>
            </b-col>
            <csp-record-regress-data v-if="showRegressData" :cols="4"
                :form-context="initialFormContexts.regressDataBasedata"/>
        </b-row>
    </div>
</template>

<script>
    import Container from '@services/Container';
    import FormDataMixin from '@desktop/components/mixins/FormDataMixin';
    import {formatDate, formatDateTime} from '@js/functions/date';
    import CostUnitBankAccount from '@desktop/components/record/cost-unit/CostUnitBankAccount.vue';
    import CostUnitList from '@desktop/components/record/cost-unit/RecordCostUnitList.vue';
    import CostUnitSummary from '@desktop/components/record/cost-unit/RecordCostUnitSummary.vue';
    import CostUnitCreate from '@desktop/components/record/cost-unit/RecordCostUnitCreateModal.vue';
    import CostUnitAddPaymentModal from '@desktop/components/record/cost-unit/RecordCostUnitAddPaymentModal.vue';
    import CostUnitEditModal from '@desktop/components/record/cost-unit/RecordCostUnitEditModal.vue';
    import CostUnitDraftEditModal from '@desktop/components/record/cost-unit/RecordCostUnitDraftEditModal.vue';
    import CostUnitDraftDetailModal from '@desktop/components/record/cost-unit/RecordCostUnitDraftDetailModal.vue';
    import CostUnitDetailModal from '@desktop/components/record/cost-unit/RecordCostUnitDetailModal.vue';
    import '@desktop/scss/components/record/cost-units.scss';
    import FormContext from '@desktop/components/FormContext';
    import PaymentRequestModal from '@desktop/components/record/cost-unit/PaymentRequestModal.vue';
    import RecordRegressData from '@desktop/components/record/cost-unit/RecordRegressData.vue';
    import RecordLawsuit from '@desktop/components/record/cost-unit/RecordLawsuit.vue';
    import RecordAppraisalList from '@desktop/components/record/cost-unit/RecordAppraisalList.vue';

    const container = Container.getInstance();
    const recordRepository = container.getDesktopRecordRepository();
    const recordRepositoryShared = container.getRecordRepository();
    const eventBus = container.getEventBus();

    export default {
        name: 'RecordCostUnits',
        components: {
            'csp-record-detail-cost-units-list': CostUnitList,
            'csp-record-detail-cost-units-summary': CostUnitSummary,
            'csp-record-detail-cost-units-create': CostUnitCreate,
            'csp-record-detail-cost-unit-add-payment-modal': CostUnitAddPaymentModal,
            'csp-record-detail-cost-unit-edit-modal': CostUnitEditModal,
            'csp-record-detail-cost-unit-draft-edit-modal': CostUnitDraftEditModal,
            'csp-record-detail-cost-unit-draft-detail-modal': CostUnitDraftDetailModal,
            'csp-record-detail-cost-unit-detail-modal': CostUnitDetailModal,
            'csp-record-detail-cost-unit-bank-account': CostUnitBankAccount,
            'csp-payment-request-modal': PaymentRequestModal,
            'csp-record-regress-data': RecordRegressData,
            'csp-record-lawsuit': RecordLawsuit,
            'csp-record-appraisal-list': RecordAppraisalList,
        },
        mixins: [FormDataMixin],
        data() {
            const recordId = parseInt(this.$route.params.recordId);
            return {
                recordId,
                costUnitListLoading: 0,
                regressDataFormLoading: true,
                costUnits: [],
                costUnitDrafts: [],
                costUnitSums: [],
                appraiserBankAccount: null,
                carDealerBankAccount: null,
                customerBankAccount: null,
                recordLawsuit: null,
                appraisals: null,
                contextData: null,
                hideForGroupOptions: null,
                detailModalCostUnitId: null,
                detailModalCostUnitDraftId: null,
                createModalAvailable: true,
                shouldShowCostUnitDetailModal: false,
                shouldShowCostUnitDraftDetailModal: false,
                editModalCostUnitId: null,
                editModalCostUnitDraftId: null,
                shouldShowCostUnitEditModal: false,
                shouldShowCostUnitDraftEditModal: false,
                addPaymentModalCostUnitId: null,
                shouldShowAddPaymentModal: false,
                sapPaymentsAjaxUrl: `/api/desktop/sappayments/${recordId}`,
                permissions: {
                    canCreateCostUnit: false,
                    canShowPaymentRequests: false,
                    canShowSapPayments: false,
                    canCreateSapPayments: false,
                    canShowRegressData: false,
                },
                creationFormContext: new FormContext({
                    name: 'cost-unit-create',
                    objectId: this.recordId,
                    reloadFields: [],
                    handleEditModeByQuery: false,
                    isInEditMode: true,
                }),
                costUnitFormContext: new FormContext({
                    name: 'cost-unit',
                    reloadFields: [],
                    handleEditModeByQuery: false,
                    isInEditMode: true,
                }),
                costUnitDraftFormContext: new FormContext({
                    name: 'cost-unit-draft',
                    reloadFields: [],
                    handleEditModeByQuery: false,
                    isInEditMode: true,
                }),
                costUnitPaymentFormContext: new FormContext({
                    name: 'cost-unit-payment-create',
                    reloadFields: [],
                    handleEditModeByQuery: false,
                    isInEditMode: true,
                }),
                initialFormContexts: {
                    regressDataBasedata: new FormContext({
                        name: 'regressDataBasedata',
                        objectId: recordId,
                        reloadFields: ['lawsuitResult'],
                    }),
                    recordLawsuit: new FormContext({
                        name: 'recordLawsuit',
                        objectId: recordId,
                        reloadFields: [],
                    }),
                },
            }
        },
        computed: {
            hasBankAccountToShow() {
                return this.appraiserBankAccount || this.carDealerBankAccount || this.customerBankAccount;
            },
            showRegressData() {
                return this.permissions.canShowRegressData && !this.regressDataFormLoading;
            },
        },
        created() {
            this.loadCostUnits().then(() => {
                if (this.permissions.canShowRegressData) {
                    this.loadRegressDataForm();
                }
            });
            this.loadHideForGroupOptions();
        },
        mounted() {
            eventBus.on('cost-unit-payments-created', this.loadCostUnits);
            eventBus.on('cost-unit-payments-created', this.reloadSapPayments);
            eventBus.on('sap-payment-changed', this.loadCostUnits);
            eventBus.on('sap-payment-changed', this.reloadSapPayments);
            eventBus.on('sap-payment-change', this.initSapPaymentsLoading);
        },
        beforeUnmount() {
            eventBus.off('cost-unit-payments-created', this.loadCostUnits);
            eventBus.off('cost-unit-payments-created', this.reloadSapPayments);
            eventBus.off('sap-payment-changed', this.loadCostUnits);
            eventBus.off('sap-payment-changed', this.reloadSapPayments);
            eventBus.off('sap-payment-change', this.initSapPaymentsLoading);
        },
        methods: {
            formatDate,
            formatDateTime,
            addCarDealerPayment(costUnitId) {
                this.addPaymentModalCostUnitId = parseInt(costUnitId);
                this.shouldShowAddPaymentModal = true;
            },
            showCostUnitDetails(costUnitId) {
                if (costUnitId.startsWith('draft-')) {
                    this.detailModalCostUnitDraftId = parseInt(costUnitId.replace('draft-', ''));
                    this.shouldShowCostUnitDraftDetailModal = true;
                } else {
                    this.detailModalCostUnitId = parseInt(costUnitId);
                    this.shouldShowCostUnitDetailModal = true;
                }
            },
            showCostUnitEdit(costUnitId) {
                if (costUnitId.startsWith('draft-')) {
                    this.editModalCostUnitDraftId = parseInt(costUnitId.replace('draft-', ''));
                    this.shouldShowCostUnitDraftEditModal = true;
                } else {
                    this.editModalCostUnitId = parseInt(costUnitId);
                    this.shouldShowCostUnitEditModal = true;
                }
            },
            deleteCostUnit(costUnitId) {
                if (!confirm('Möchten Sie diese Schadenposition wirklich löschen?')) {
                    return;
                }
                this.costUnitListLoading++;
                recordRepository.deleteCostUnit(this.recordId, costUnitId).then(() => {
                    this.loadCostUnits();
                    this.costUnitListLoading--;
                });
            },
            verifyCostUnit(costUnitId) {
                this.costUnitListLoading++;
                recordRepository.verifyCostUnit(this.recordId, costUnitId).then(() => {
                    this.loadCostUnits();
                    this.costUnitListLoading--;
                });
            },
            loadCostUnits() {
                this.costUnitListLoading++;

                return recordRepository.loadCostUnits(this.recordId).then((response) => {
                    const attributes = response.data.attributes;

                    this.contextData = attributes.contextData;
                    this.costUnits = attributes.costUnits.data;
                    this.costUnitDrafts = attributes.costUnitDrafts.data;
                    this.costUnitSums = attributes.costUnitSums.data?.attributes;
                    this.appraiserBankAccount = attributes.appraiserBankAccount.data;
                    this.carDealerBankAccount = attributes.carDealerBankAccount.data;
                    this.customerBankAccount = attributes.customerBankAccount.data;
                    this.recordLawsuit = attributes.recordLawsuit.data;
                    this.appraisals = attributes.appraisals.data;
                    this.permissions = response.data.meta.permissions;

                    if (this.recordLawsuit) {
                        // We need to manually apply server data to formContext, as we load data "manually" form backend
                        // and not via loadFormData method (as multiple data are not form related)
                        this.applyServerData(this.recordLawsuit);
                    }
                }).finally(() => {
                    this.costUnitListLoading--;
                });
            },
            loadRegressDataForm() {
                const loadFunction = () => {
                    return recordRepository.loadRegressDataForm(this.recordId);
                };

                this.loadFormData(loadFunction).finally(() => {
                    this.regressDataFormLoading = false;
                });
            },
            loadHideForGroupOptions() {
                this.costUnitListLoading++;
                recordRepositoryShared.hideForGroupOptions(this.recordId).then(
                    (options) => {
                        this.hideForGroupOptions = options;
                    }
                ).finally(() => {
                    this.costUnitListLoading--;
                });
            },
            reloadSapPayments() {
                this.$refs.sapPaymentsTable.loadRecords();
            },
            initSapPaymentsLoading() {
                this.$refs.sapPaymentsTable.isLoading = true;
            },
            resetDetailModal() {
                this.detailModalCostUnitId = null;
                this.shouldShowCostUnitDetailModal = false;
                this.detailModalCostUnitDraftId = null;
                this.shouldShowCostUnitDraftDetailModal = false;
            },
            resetAddCarDealerPaymentModal() {
                this.addPaymentModalCostUnitId = null;
                this.shouldShowAddPaymentModal = false;
                this.costUnitPaymentFormContext = new FormContext({
                    name: 'cost-unit-payment-create',
                    reloadFields: [],
                    handleEditModeByQuery: false,
                    isInEditMode: true,
                });
            },
            resetCreateModal() {
                this.createModalAvailable = false;
                this.$nextTick(() => {
                    this.createModalAvailable = true;
                });
                this.creationFormContext = new FormContext({
                    name: 'cost-unit-create',
                    objectId: this.recordId,
                    reloadFields: [],
                    handleEditModeByQuery: false,
                    isInEditMode: true,
                });
            },
            resetEditModal() {
                this.editModalCostUnitId = null;
                this.editModalCostUnitDraftId = null;
                this.shouldShowCostUnitEditModal = false;
                this.costUnitFormContext = new FormContext({
                    name: 'cost-unit',
                    reloadFields: [],
                    handleEditModeByQuery: false,
                    isInEditMode: true,
                });
                this.costUnitDraftFormContext = new FormContext({
                    name: 'cost-unit-draft',
                    reloadFields: [],
                    handleEditModeByQuery: false,
                    isInEditMode: true,
                });
            },
            sapPaymentsRowClass(item, type) {
                if (type !== 'row' || !item) {
                    return;
                }

                if (item.shouldBeGrey) {
                    return 'text-muted';
                }
            },
            createCostUnitPaymentsFromSap() {
                this.costUnitListLoading++;
                recordRepository.createCostUnitPaymentsFromSap(this.recordId)
                    .then(() => {
                        this.loadCostUnits();
                        this.costUnitListLoading--;
                    });
            },
        },
    }
</script>
